import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import Symbols from '../../../../utilities/mappings/symbols/Symbols';
import SYMBOL_SYNONYMS from '../../../../utilities/constants/symbolSynonyms';
import { client } from '../../../../api/client';
import { addAlert } from '../alerts/alertSlice';
import { EDITOR_TAB_PANEL_ELEMENTS } from '../../../../utilities/constants/editorConstants';


const editorAdapter = createEntityAdapter();
const SEARCHABLE_SYMBOL_CATEGORIES = new Set([
  'electricEnergySuppliers',
  'twoWindingTransformers',
  'threeWindingTransformers',
  'generators',
  'electronicRelays',
  'electromagneticSwitches',
  'fuses',
  'overloadRelays',
  'vfds',
  'cables',
  'buses',
  'distributionBoards',
  'lightningContactChargingCenters',
  'textBoxes',
  'subdiagram',
]);

const clonePlain = (item) => JSON.parse(JSON.stringify(item));

const normalizeAttributesVisibility = (attributes) => {
  if (!attributes || typeof attributes !== 'object') {
    return attributes;
  }
  return Object.entries(attributes).reduce((acc, [key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      acc[key] = {
        ...value,
        isVisible: true,
        visibilityUserModified: value.visibilityUserModified === true,
      };
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const serializeSymbols = (symbols) => {
  return Object.keys(symbols).reduce((acc, category) => {
    const list = symbols[category] || [];
    const expanded = list.flatMap((item) => {
      const plain = clonePlain(item);
      plain.attributes = normalizeAttributesVisibility(plain.attributes);
      const variants = Array.isArray(plain?.variants) ? plain.variants : [];
      const normalizedVariants = variants.map((variant) => ({
        ...variant,
        attributes: normalizeAttributesVisibility(variant.attributes),
      }));
      const baseAlias = plain.alias;
      const baseLabel = plain.label;

      if (!normalizedVariants.length) {
        return [{
          ...plain,
          baseAlias,
          baseLabel,
          variantAlias: null,
          displayLabel: plain.label,
          activeVariantAlias: null,
          variantLabel: null,
        }];
      }

      return normalizedVariants.map((variant, index) => ({
        ...plain,
        alias: variant.alias || `${baseAlias}__variant_${index}`,
        baseAlias,
        baseLabel,
        variantAlias: variant.alias || null,
        variantLabel: variant.label || null,
        displayLabel: variant.label ? `${baseLabel} - ${variant.label}` : baseLabel,
        label: variant.label ? `${baseLabel} - ${variant.label}` : baseLabel,
        svgSrc: variant.diagramSvgSrc || plain.svgSrc,
        activeVariantAlias: variant.alias || null,
        attributes: normalizeAttributesVisibility(variant.attributes || plain.attributes),
        variants: normalizedVariants.map((entry) => ({
          ...entry,
          selected: entry.alias === variant.alias,
        })),
      }));
    });

    acc[category] = expanded;
    return acc;
  }, {});
};

const normalizeForSearch = (value = '') => {
  return value
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[-_/]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const buildSymbolTokens = (item) => {
  const tokens = [
    item?.label,
    item?.alias,
    item?.displayLabel,
    item?.baseAlias,
    item?.baseLabel,
    item?.variantLabel,
  ];

  if (Array.isArray(item?.keywords)) {
    tokens.push(...item.keywords);
  }

  if (Array.isArray(item?.synonyms)) {
    tokens.push(...item.synonyms);
  }

  const aliasSynonyms = SYMBOL_SYNONYMS[item?.alias];
  if (Array.isArray(aliasSynonyms)) {
    tokens.push(...aliasSynonyms);
  }
  if (item?.baseAlias && SYMBOL_SYNONYMS[item.baseAlias]) {
    tokens.push(...SYMBOL_SYNONYMS[item.baseAlias]);
  }

  return tokens
    .filter(Boolean)
    .map((token) => normalizeForSearch(token))
    .filter(Boolean);
};

const matchesQuery = (item, normalizedQuery) => {
  if (!normalizedQuery) return false;
  const searchableTokens = buildSymbolTokens(item);
  return searchableTokens.some((token) => token.includes(normalizedQuery));
};

const collectSearchResults = (symbols, normalizedQuery) => {
  if (!normalizedQuery) return [];
  return Object.entries(symbols)
    .filter(([category]) => SEARCHABLE_SYMBOL_CATEGORIES.has(category))
    .flatMap(([category, items]) =>
      items
        .filter((item) => matchesQuery(item, normalizedQuery))
        .map((item) => ({
          ...item,
          category,
        }))
    );
};

const initialState = editorAdapter.getInitialState({
  status: 'idle',
  angleOffset: 0,
  tabPanel: EDITOR_TAB_PANEL_ELEMENTS,
  categories: {
    transformers: { open: false },
    energySources: { open: true },
    charges: { open: false },
    electronicPotency: { open: false },
    protectionDevices: { open: false },
    filters: { open: false },
    misc: { open: false },
    medicion: { open: false },
  },
  symbols: serializeSymbols(Symbols),
  searchQuery: '',
  searchResults: [],
  forceSearchResults: false,
  searchSelectedIndex: 0,
});

const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    setOrientation(state, action) {
      state.angleOffset = action.payload.angle;
    },
    setTabPanel(state, action) {
      state.tabPanel = action.payload.tabPanel;
    },
    setCategoryOpen(state, action) {
      state.categories[action.payload.category].open = action.payload.open;
    },
    setExclusiveCategoryOpen(state, action) {
      const { category, open } = action.payload || {};
      Object.keys(state.categories).forEach((key) => {
        state.categories[key].open = key === category ? !!open : false;
      });
    },
    refreshSymbols(state) {
      state.symbols = serializeSymbols(Symbols);
      const normalizedQuery = normalizeForSearch(state.searchQuery || '');
      state.searchResults = collectSearchResults(state.symbols, normalizedQuery);
    },
    search(state, action) {
      const rawQuery = action.payload.query || '';
      const normalizedQuery = normalizeForSearch(rawQuery);
      state.searchQuery = rawQuery;
      state.searchResults = collectSearchResults(state.symbols, normalizedQuery);
      state.searchSelectedIndex = 0;
    },
    setForceSearchResults(state, action) {
      state.forceSearchResults = !!action.payload;
    },
    setSearchSelectedIndex(state, action) {
      const idx = Number(action.payload);
      state.searchSelectedIndex = isNaN(idx) ? 0 : Math.max(0, idx);
    }        
  }
});

export const {
  setOrientation,
  setTabPanel,
  setCategoryOpen,
  setExclusiveCategoryOpen,
  refreshSymbols,
  search,
  setForceSearchResults,
  setSearchSelectedIndex
} = editorSlice.actions;

export default editorSlice.reducer;
